import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/auth';
import { useTheme } from '@/theme';
import { apiConfig } from '@/config';
import { apiClient } from '@/api/client';
import { settingsService } from '@/api/services/settings.service';
import { storeNdToken } from '@/utils/ndTokenManager';
import { useNetDocumentsStore } from '@/store/useNetDocumentsStore';
import { useSignalR } from '@/realtime/useSignalR';
import type { NDTokenData } from '@/utils/ndTokenManager';

import ndRedIcon from '@/assets/images/icon_ndred.png';
import ndBlueIcon from '@/assets/images/icon_ndblue.png';
import dsRedIcon from '@/assets/images/DocuSign_red.png';
import dsBlueIcon from '@/assets/images/DocuSign_blue.png';
import { useDocuSignStore } from '@/store/useDocuSignStore';

/** Poll cadence / ceiling for the AuthStatus fallback (100 * 3s = 5 minutes). */
const AUTH_POLL_INTERVAL_MS = 3000;
const AUTH_POLL_MAX_ATTEMPTS = 100;

/** Build custom auth headers matching Angular's DocuSignService.getAuthHeaders */
function buildProviderHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  try {
    const ds = JSON.parse(localStorage.getItem('DSToken') || '{}');
    if (ds.access_token) headers['ds_access_token'] = ds.access_token;
  } catch {}
  try {
    const nd = JSON.parse(localStorage.getItem('NDToken') || '{}');
    if (nd.access_token) headers['nd_access_token'] = nd.access_token;
  } catch {}
  try {
    const im = JSON.parse(localStorage.getItem('imanage-token') || '{}');
    if (im.access_token) headers['Imanage_access_token'] = im.access_token;
  } catch {}
  return headers;
}

/**
 * NetDocuments & DocuSign authentication buttons displayed at the bottom of the sidebar.
 * Mirrors the Angular menu/docusign and menu/netdocumnet components.
 *
 * OAuth token delivery (must match the backend NDApiController/DSConnectController):
 *  1. Primary  — SignalR `ReceiveAuthComplete` push ({ provider, token }).
 *  2. Fallback — poll `/NDApi/AuthStatus` / `/DSConnect/DSAuthStatus` (used when
 *                SignalR is disconnected).
 *  3. Legacy   — `postMessage` from the popup (only when the backend has no valid
 *                OAuth state and therefore can't push via SignalR).
 * The backend SKIPS postMessage whenever it can push via SignalR (Zscaler Browser
 * Isolation blocks cross-window postMessage), so (1)/(2) are required — postMessage
 * alone is not enough.
 */
export const SidebarAuthButtons = ({ collapsed }: { collapsed: boolean }) => {
  const { hasPermission } = useAuth();
  const { theme } = useTheme();
  const { connection } = useSignalR();

  // ND auth state from the singleton store (validated once on app load)
  const ndAuthenticated = useNetDocumentsStore((s) => s.isAuthenticated);

  const [dsAuthenticated, setDsAuthenticated] = useState(false);
  const [dsUser, setDsUser] = useState<string>('');
  const [ndRedirectUrl, setNdRedirectUrl] = useState<string>('');

  // OAuth popup windows + polling timers (per provider) so we can clean them up.
  const ndWindowRef = useRef<Window | null>(null);
  const dsWindowRef = useRef<Window | null>(null);
  const ndPollRef = useRef<number | null>(null);
  const dsPollRef = useRef<number | null>(null);

  const allowNd = hasPermission('motr.Application.AllowNDConnection');
  const allowDs = hasPermission('motr.Application.AllowDSConnection');

  // Fetch ND redirect URL setting
  useEffect(() => {
    if (!allowNd) return;
    settingsService
      .getSettings('motr.NetDocument.RedirectURI')
      .then((settings) => {
        const s = settings.find((x) => x.name === 'motr.NetDocument.RedirectURI');
        if (s?.value) setNdRedirectUrl(s.value);
      })
      .catch(() => {});
  }, [allowNd]);

  // Check DocuSign auth on mount
  useEffect(() => {
    if (!allowDs) return;
    checkDsAuth();
  }, [allowDs]);

  const checkDsAuth = useCallback(async () => {
    try {
      const { data: result } = await apiClient.get('/DSConnect/GetUserInfo', {
        headers: buildProviderHeaders(),
      });
      if (result && !result.error) {
        setDsAuthenticated(true);
        setDsUser(result.displayName || '');
      }
    } catch {}
  }, []);

  // ── OAuth token delivery: stop watching a provider once we have its token ──
  const stopNdAuthWatch = useCallback(() => {
    if (ndPollRef.current !== null) {
      clearInterval(ndPollRef.current);
      ndPollRef.current = null;
    }
    if (ndWindowRef.current && !ndWindowRef.current.closed) {
      try { ndWindowRef.current.close(); } catch { /* ignore */ }
    }
    ndWindowRef.current = null;
  }, []);

  const stopDsAuthWatch = useCallback(() => {
    if (dsPollRef.current !== null) {
      clearInterval(dsPollRef.current);
      dsPollRef.current = null;
    }
    if (dsWindowRef.current && !dsWindowRef.current.closed) {
      try { dsWindowRef.current.close(); } catch { /* ignore */ }
    }
    dsWindowRef.current = null;
  }, []);

  /** Store an ND token (JSON string) delivered via SignalR, polling, or postMessage. */
  const processNdToken = useCallback((tokenJson: string) => {
    try {
      const token = JSON.parse(tokenJson);
      // ND tokens always carry an authorization_code; ignore anything else.
      if (!token || token.authorization_code == null) return;
      storeNdToken(token as NDTokenData); // stamps expires_on + flips the store → icon turns blue
      stopNdAuthWatch();
    } catch { /* not JSON / not our payload */ }
  }, [stopNdAuthWatch]);

  /** Store a DocuSign token (JSON string) delivered via SignalR, polling, or postMessage. */
  const processDsToken = useCallback((tokenJson: string) => {
    try {
      const token = JSON.parse(tokenJson);
      if (!token || !token.access_token) return;
      token.expires_on = new Date(Date.now() + token.expires_in * 1000).toISOString();
      token.expiresAt = Date.now() + token.expires_in * 1000;
      localStorage.setItem('DSToken', JSON.stringify(token));
      setDsAuthenticated(true);
      // The document Actions menus read the shared store, not this component's state;
      // storage events don't fire in the tab that wrote the token, so sync it here.
      void useDocuSignStore.getState().validateTokenWithServer();
      checkDsAuth();
      stopDsAuthWatch();
    } catch { /* not JSON / not our payload */ }
  }, [checkDsAuth, stopDsAuthWatch]);

  // ── Fallback: poll the server for the cached token (SignalR disconnected) ──
  const startNdAuthPolling = useCallback(() => {
    if (ndPollRef.current !== null) return; // already polling
    let attempts = 0;
    ndPollRef.current = window.setInterval(async () => {
      attempts += 1;
      if (attempts > AUTH_POLL_MAX_ATTEMPTS) { stopNdAuthWatch(); return; }
      try {
        const { data } = await apiClient.get<{ completed?: boolean; token?: string }>(
          '/NDApi/AuthStatus',
        );
        if (data?.completed && data.token) processNdToken(data.token);
      } catch { /* keep polling */ }
    }, AUTH_POLL_INTERVAL_MS);
  }, [processNdToken, stopNdAuthWatch]);

  const startDsAuthPolling = useCallback(() => {
    if (dsPollRef.current !== null) return;
    let attempts = 0;
    dsPollRef.current = window.setInterval(async () => {
      attempts += 1;
      if (attempts > AUTH_POLL_MAX_ATTEMPTS) { stopDsAuthWatch(); return; }
      try {
        const { data } = await apiClient.get<{ completed?: boolean; token?: string }>(
          '/DSConnect/DSAuthStatus',
        );
        if (data?.completed && data.token) processDsToken(data.token);
      } catch { /* keep polling */ }
    }, AUTH_POLL_INTERVAL_MS);
  }, [processDsToken, stopDsAuthWatch]);

  // Primary: token pushed from the backend via SignalR after OAuth completes.
  useEffect(() => {
    if (!connection) return;
    const handler = (data: { provider?: string; token?: string }) => {
      if (!data?.token) return;
      if (data.provider === 'netdocuments') processNdToken(data.token);
      else if (data.provider === 'docusign') processDsToken(data.token);
    };
    connection.on('ReceiveAuthComplete', handler);
    return () => { connection.off('ReceiveAuthComplete', handler); };
  }, [connection, processNdToken, processDsToken]);

  // Legacy fallback: token delivered via postMessage from the OAuth popup
  // (only fires when the backend had no valid state and could not push via SignalR).
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e?.data || typeof e.data !== 'string' || e.data === '') return;
      let token: { authorization_code?: unknown; access_token?: unknown } | null = null;
      try {
        token = JSON.parse(e.data);
      } catch {
        return;
      }
      if (!token) return;

      // NetDocuments token: has the authorization_code key. DocuSign: access_token only.
      if ('authorization_code' in token) {
        processNdToken(e.data);
      } else if (token.access_token) {
        processDsToken(e.data);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [processNdToken, processDsToken]);

  // Reflect a connection made from a document Actions menu (shared store) in this icon.
  const storeDsAuthenticated = useDocuSignStore((s) => s.isAuthenticated);
  useEffect(() => {
    if (storeDsAuthenticated) checkDsAuth();
  }, [storeDsAuthenticated, checkDsAuth]);

  // Listen for DS storage changes (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'DSToken') {
        checkDsAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkDsAuth]);

  // Stop any in-flight polling / popups when the component unmounts.
  useEffect(() => () => {
    stopNdAuthWatch();
    stopDsAuthWatch();
  }, [stopNdAuthWatch, stopDsAuthWatch]);

  const openDsAuth = async () => {
    const baseUrl = `${apiConfig.baseUrl}/DSConnect/GetAuthorizationCode`;
    // Open synchronously (keeps the click's user activation, so it isn't popup-blocked),
    // then point it at the auth URL once we have a state.
    const popup = window.open(
      'about:blank',
      'docuSignWindow',
      'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=500,height=500'
    );
    dsWindowRef.current = popup;
    // Request the OAuth state with our bearer token. The popup itself only carries the
    // server cookie, which during impersonation is the impersonator — the token would then
    // be delivered to the wrong user and this session would never receive it.
    let url = baseUrl;
    try {
      const { data } = await apiClient.get<{ state?: string }>('/DSConnect/GetAuthState');
      if (data?.state) url = `${baseUrl}?state=${encodeURIComponent(data.state)}`;
    } catch { /* fall back to cookie-derived state */ }
    if (popup && !popup.closed) popup.location.href = url;
    startDsAuthPolling();
  };

  const openNdAuth = () => {
    const url = ndRedirectUrl
      ? `${ndRedirectUrl}/NDApi/GetAuthorizationCode`
      : `${apiConfig.baseUrl}/NDApi/GetAuthorizationCode`;
    ndWindowRef.current = window.open(
      url,
      'ndWindow',
      'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=500,height=500'
    );
    startNdAuthPolling();
  };

  if (!allowNd && !allowDs) return null;

  const imgStyle: React.CSSProperties = {
    maxWidth: '30px',
    maxHeight: '30px',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: collapsed ? 'column' : 'row',
        alignItems: 'center',
        gap: collapsed ? theme.spacing.sm : theme.spacing.md,
        padding: collapsed ? `${theme.spacing.sm} 0` : `${theme.spacing.md} ${theme.spacing.lg}`,
        borderTop: `1px solid ${theme.sidebar.border}`,
        flexShrink: 0,
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}
    >
      {allowNd && (
        <img
          src={ndAuthenticated ? ndBlueIcon : ndRedIcon}
          alt={ndAuthenticated ? 'NetDocuments Connected' : 'NetDocuments Not Connected'}
          title={ndAuthenticated ? 'Connected to NetDocuments' : 'Connect to NetDocuments'}
          style={imgStyle}
          onClick={openNdAuth}
        />
      )}
      {allowDs && (
        <img
          src={dsAuthenticated ? dsBlueIcon : dsRedIcon}
          alt={dsAuthenticated ? 'DocuSign Connected' : 'DocuSign Not Connected'}
          title={dsAuthenticated && dsUser ? dsUser : 'Connect to DocuSign'}
          style={imgStyle}
          onClick={openDsAuth}
        />
      )}
    </div>
  );
};
